/* LouCapsuleWardrobe — Generate a personalized capsule wardrobe (flat-lay image + item list).
 * User provides 3-4 free-text params (klädarketyp, färgsäsong, stiltyp, årstid-fokus).
 * Backend uses Claude for structured list + Gemini Nano Banana for the flat-lay image.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, ShirtIcon, Wand2, Check, ShoppingBag, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Swedish-shop search URLs — keyword is the item name
const SHOPS = [
  { id: 'hm', label: 'H&M', url: (q) => `https://www2.hm.com/sv_se/sok-resultat.html?q=${encodeURIComponent(q)}`, color: 'bg-rose-500 hover:bg-rose-600' },
  { id: 'zalando', label: 'Zalando', url: (q) => `https://www.zalando.se/catalog/?q=${encodeURIComponent(q)}`, color: 'bg-orange-500 hover:bg-orange-600' },
  { id: 'sellpy', label: 'Sellpy', url: (q) => `https://www.sellpy.se/search?q=${encodeURIComponent(q)}`, color: 'bg-emerald-600 hover:bg-emerald-700' },
];

// Curated suggestion chips so the user doesn't have to remember the lingo
const ARCHETYPES = ['Sage', 'Mother', 'Lover', 'Heroine', 'Maiden', 'Mystic', 'Queen', 'Innocent', 'Creative'];
const SEASONS = ['Dark Autumn', 'True Autumn', 'Soft Autumn', 'Dark Winter', 'True Winter', 'Bright Winter', 'Light Spring', 'True Spring', 'Bright Spring', 'Soft Summer', 'True Summer', 'Light Summer'];
const STYLE_TYPES = ['Soft Classic', 'Classic', 'Dramatic Classic', 'Soft Natural', 'Natural', 'Flamboyant Natural', 'Soft Dramatic', 'Dramatic', 'Theatrical Romantic', 'Romantic', 'Soft Gamine', 'Gamine'];
const BODY_SHAPES = ['Timglas', 'Päron', 'Äpple', 'Rektangel', 'Omvänd triangel', 'Sandklocka mjuk'];
const FOCUS_SEASONS = ['höst', 'vinter', 'vår', 'sommar', 'året runt'];

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-bold px-2.5 py-1 rounded-full border-2 transition ${
        active
          ? 'bg-violet-600 border-violet-600 text-white shadow'
          : 'bg-white border-violet-200 text-violet-700 hover:border-violet-400'
      }`}
    >
      {label}
    </button>
  );
}

export default function LouCapsuleWardrobe({ onClose }) {
  const [archetype, setArchetype] = useState('');
  const [season, setSeason] = useState('');
  const [styleType, setStyleType] = useState('');
  const [bodyShape, setBodyShape] = useState('');
  const [focus, setFocus] = useState('');
  const [notes, setNotes] = useState('');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [togglingIdx, setTogglingIdx] = useState(null);

  const toggleOwned = async (idx) => {
    if (!result || togglingIdx !== null) return;
    const cur = result.items[idx];
    const nextVal = !cur.owned;
    // Optimistic update
    setTogglingIdx(idx);
    setResult((r) => ({
      ...r,
      items: r.items.map((it, i) => (i === idx ? { ...it, owned: nextVal } : it)),
    }));
    try {
      await axios.post(`${API}/experts/lou/capsule/toggle`, { index: idx, owned: nextVal }, { withCredentials: true });
    } catch (e) {
      // Revert on failure
      setResult((r) => ({
        ...r,
        items: r.items.map((it, i) => (i === idx ? { ...it, owned: cur.owned } : it)),
      }));
      toast.error('Kunde inte spara');
    } finally {
      setTogglingIdx(null);
    }
  };

  // Load existing capsule on open
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/experts/lou/capsule`, { withCredentials: true });
        const c = r.data?.capsule;
        if (c) {
          setResult(c);
          setArchetype(c.archetype || '');
          setSeason(c.color_season || '');
          setStyleType(c.style_type || '');
          setBodyShape(c.body_shape || '');
          setFocus(c.season_focus || '');
          setNotes(c.notes || '');
        }
      } catch {/**/}
    })();
  }, []);

  const generate = async () => {
    if (busy) return;
    if (!archetype.trim() && !season.trim() && !styleType.trim()) {
      toast.error('💜 Fyll i åtminstone en parameter (arketyp, säsong eller stil)');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/experts/lou/capsule`, {
        archetype, color_season: season, style_type: styleType, body_shape: bodyShape, season_focus: focus, notes,
      }, { withCredentials: true, timeout: 120000 });
      setResult(r.data);
      toast.success('✨ Din kapselgarderob är klar 💜');
    } catch (e) {
      const det = e?.response?.data?.detail || '';
      if (e?.response?.status === 503) toast.error('LLM-nyckel saknas — toppa upp');
      else toast.error('Kunde inte generera: ' + det);
    } finally { setBusy(false); }
  };

  const palette = result?.palette || [];
  const items = result?.items || [];

  return (
    <div
      className="fixed inset-0 z-[345] bg-black/75 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="capsule-modal"
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl bg-[#FAF6F0] max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="px-4 py-3 bg-gradient-to-r from-stone-100 to-rose-50 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShirtIcon className="w-4 h-4 text-stone-700" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Lou · stylist</p>
              <h2 className="font-serif text-2xl font-bold text-stone-800 leading-tight">Personlig kapselgarderob</h2>
              <p className="text-[11px] text-stone-500 italic">Baserad på din arketyp, färgårstid, stiltyp och daglig vardag</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-200" data-testid="capsule-close">
            <X className="w-5 h-5 text-stone-700" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Form */}
          <section className="space-y-3" data-testid="capsule-form">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Klädarketyp</label>
              <input
                value={archetype}
                onChange={(e) => setArchetype(e.target.value)}
                placeholder="t.ex. Sage, Mother, Lover…"
                data-testid="capsule-archetype-input"
                className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm bg-white"
              />
              <div className="flex flex-wrap gap-1">
                {ARCHETYPES.map((a) => (
                  <Chip key={a} label={a} active={archetype.toLowerCase() === a.toLowerCase()} onClick={() => setArchetype(a)} testid={`archetype-${a.toLowerCase()}`} />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Färgårstid</label>
              <input
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                placeholder="t.ex. True Autumn, Soft Summer, Dark Winter…"
                data-testid="capsule-season-input"
                className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm bg-white"
              />
              <div className="flex flex-wrap gap-1">
                {SEASONS.map((s) => (
                  <Chip key={s} label={s} active={season.toLowerCase() === s.toLowerCase()} onClick={() => setSeason(s)} testid={`season-${s.toLowerCase().replace(/\s+/g, '-')}`} />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Stil / Kibbe-typ</label>
              <input
                value={styleType}
                onChange={(e) => setStyleType(e.target.value)}
                placeholder="t.ex. Soft Classic, Dramatic, Romantic…"
                data-testid="capsule-style-input"
                className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm bg-white"
              />
              <div className="flex flex-wrap gap-1">
                {STYLE_TYPES.map((s) => (
                  <Chip key={s} label={s} active={styleType.toLowerCase() === s.toLowerCase()} onClick={() => setStyleType(s)} testid={`style-${s.toLowerCase().replace(/\s+/g, '-')}`} />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Kroppsform (smickrar silhuetterna)</label>
              <input
                value={bodyShape}
                onChange={(e) => setBodyShape(e.target.value)}
                placeholder="t.ex. Timglas, Päron, Rektangel…"
                data-testid="capsule-bodyshape-input"
                className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm bg-white"
              />
              <div className="flex flex-wrap gap-1">
                {BODY_SHAPES.map((b) => (
                  <Chip key={b} label={b} active={bodyShape.toLowerCase() === b.toLowerCase()} onClick={() => setBodyShape(b)} testid={`bodyshape-${b.toLowerCase().replace(/\s+/g, '-')}`} />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Årstid att klä mig för (valfritt)</label>
              <div className="flex flex-wrap gap-1">
                {FOCUS_SEASONS.map((s) => (
                  <Chip key={s} label={s} active={focus === s} onClick={() => setFocus(focus === s ? '' : s)} testid={`focus-${s}`} />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Extra önskemål (valfritt)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                placeholder="t.ex. inga skjortor, gillar långa kjolar, sneakers istället för loafers…"
                data-testid="capsule-notes-input"
                rows={2}
                className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm bg-white resize-none"
              />
            </div>

            <button
              onClick={generate}
              disabled={busy}
              data-testid="capsule-generate-btn"
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-700 hover:to-rose-600 text-white font-extrabold text-sm shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {busy ? 'Lou bygger din garderob… (~30–60 sek)' : (result ? '🔁 Generera ny kapselgarderob' : '✨ Skapa min kapselgarderob')}
            </button>
          </section>

          {/* Result */}
          {result && (
            <section className="space-y-3 pt-2 border-t-2 border-stone-200" data-testid="capsule-result">
              <div className="flex items-baseline gap-2">
                <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
                <h3 className="font-serif text-xl font-bold text-stone-800">Din kapselgarderob</h3>
              </div>

              {/* Image */}
              {result.image_base64 ? (
                <div className="rounded-2xl overflow-hidden bg-white border border-stone-200 shadow">
                  <img
                    src={`data:${result.image_mime || 'image/png'};base64,${result.image_base64}`}
                    alt="Kapselgarderob flat-lay"
                    className="w-full h-auto block"
                    data-testid="capsule-image"
                  />
                </div>
              ) : (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 italic">
                  Bilden kunde inte genereras (LLM-budget kanske tog slut). Listan nedan är giltig — du kan generera om senare.
                </div>
              )}

              {/* Palette */}
              {palette.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Din palett</p>
                  <div className="flex gap-1.5 flex-wrap" data-testid="capsule-palette">
                    {palette.map((c, i) => (
                      <div key={i} className="flex flex-col items-center" data-testid={`capsule-palette-${i}`}>
                        <div className="w-10 h-10 rounded-full shadow border-2 border-white" style={{ backgroundColor: c }} />
                        <span className="text-[9px] text-stone-500 font-mono mt-0.5">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Item list with checkboxes + shop links */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">
                    Plagg ({items.filter((it) => it.owned).length}/{items.length} ägda)
                  </p>
                  {items.length > 0 && (
                    <div className="flex-1 mx-2 h-1.5 bg-stone-200 rounded-full overflow-hidden max-w-[180px]" data-testid="capsule-progress">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all"
                        style={{ width: `${Math.round((items.filter((it) => it.owned).length / items.length) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <ul className="space-y-1.5" data-testid="capsule-items">
                  {items.map((it, i) => (
                    <li
                      key={i}
                      className={`rounded-xl border px-2.5 py-2 transition ${
                        it.owned
                          ? 'bg-emerald-50/70 border-emerald-300'
                          : 'bg-white border-stone-200'
                      }`}
                      data-testid={`capsule-item-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          onClick={() => toggleOwned(i)}
                          data-testid={`capsule-item-toggle-${i}`}
                          disabled={togglingIdx === i}
                          aria-label={it.owned ? 'Markera som icke-ägd' : 'Markera som ägd'}
                          className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition ${
                            it.owned
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'bg-white border-stone-300 hover:border-emerald-400'
                          }`}
                        >
                          {it.owned && <Check className="w-3 h-3" strokeWidth={3} />}
                        </button>
                        <span className="text-[9px] uppercase tracking-widest font-extrabold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded shrink-0 mt-0.5">{it.slot}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`font-bold text-[12px] leading-tight ${it.owned ? 'text-stone-500 line-through' : 'text-stone-800'}`}>{it.name}</p>
                          {it.color && <p className="text-stone-500 text-[10px] italic">{it.color}</p>}
                          {it.notes && <p className="text-stone-600 text-[10px] mt-0.5">{it.notes}</p>}
                        </div>
                      </div>
                      {!it.owned && (
                        <div className="flex gap-1 mt-1.5 ml-7 flex-wrap" data-testid={`capsule-shop-${i}`}>
                          {SHOPS.map((s) => (
                            <a
                              key={s.id}
                              href={s.url(it.name)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`capsule-shop-${s.id}-${i}`}
                              className={`text-[10px] font-bold px-2 py-1 rounded-lg text-white flex items-center gap-1 transition ${s.color}`}
                            >
                              <ShoppingBag className="w-2.5 h-2.5" /> {s.label} <ExternalLink className="w-2 h-2 opacity-70" />
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
